import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Car, Send, ExternalLink } from 'lucide-react';
import { api } from '../api';
import { useToast } from '../context/ToastContext';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const data = await api.subscribe(email);
      toast(data.message, 'success');
      setEmail('');
    } catch (err) {
      toast(err.message, 'error');
    } finally { setLoading(false); }
  };

  return (
    <footer className="bg-gray-900 text-white mt-20">
      {/* Wave */}
      <div className="h-12 bg-white" style={{clipPath:'ellipse(55% 100% at 50% 0%)'}}/>

      <div className="max-w-7xl mx-auto px-6 pt-12 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center shrink-0">
                <Car size={17} className="text-white"/>
              </div>
              <div>
                <div className="font-display font-black text-xl text-white tracking-widest">ALFA</div>
                <div className="text-[10px] uppercase tracking-widest text-gray-400">Trafikskola AB</div>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Alfa Trafikskola AB i Kista erbjuder professionell körkortsutbildning med certifierade instruktörer. Org.nr 559470-5062.
            </p>
            <div className="space-y-2.5">
              {[
                [MapPin, 'Nordkapsgatan 8 Tr, 164 36 Kista'],
                [Phone, 'Abubakar: 073-754 74 05', 'tel:+46737547405'],
                [Phone, 'Ahmed: 070-481 57 43', 'tel:+46704815743'],
                [Mail, 'info@alfatrafikskola.se', 'mailto:info@alfatrafikskola.se'],
                [Clock, 'Mån–Fre 08–18 · Lör 09–14'],
              ].map(([Icon, text, href], i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-gray-400">
                  <Icon size={12} className="text-red-400 shrink-0"/>
                  {href ? <a href={href} className="hover:text-red-400 transition-colors">{text}</a> : <span>{text}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Tjänster */}
          <div>
            <h3 className="text-xs font-black uppercase tracking-widest text-white mb-4 after:content-[''] after:block after:w-6 after:h-0.5 after:bg-red-600 after:mt-1.5">Tjänster</h3>
            <ul className="space-y-2.5">
              {[['Körlektioner','/korlektioner'],['Teorilektioner','/teorilektioner'],['Teoriprov Online','/teoriprov'],['Riskutbildning Del 1','/riskutbildning-1'],['Riskutbildning Del 2','/riskutbildning-2'],['Boka Lektion','/korlektioner']].map(([l,t])=>(
                <li key={t}><Link to={t} className="text-sm text-gray-400 hover:text-red-400 transition-colors">{l}</Link></li>
              ))}
            </ul>
          </div>

          {/* Information */}
          <div>
            <h3 className="text-xs font-black uppercase tracking-widest text-white mb-4 after:content-[''] after:block after:w-6 after:h-0.5 after:bg-red-600 after:mt-1.5">Information</h3>
            <ul className="space-y-2.5 mb-5">
              {[['Om Alfa Trafikskola','/om-oss'],['Tips & Körkortsguider','/tips'],['Kontakta Oss','/kontakt'],['FAQ','/tips']].map(([l,t])=>(
                <li key={t}><Link to={t} className="text-sm text-gray-400 hover:text-red-400 transition-colors">{l}</Link></li>
              ))}
            </ul>
            <h3 className="text-xs font-black uppercase tracking-widest text-white mb-3 after:content-[''] after:block after:w-6 after:h-0.5 after:bg-red-600 after:mt-1.5">Externa Länkar</h3>
            <ul className="space-y-2">
              {[['Trafikverket','https://www.trafikverket.se'],['Körkortsteori.se','https://www.korkortonline.se']].map(([l,h])=>(
                <li key={h}><a href={h} target="_blank" rel="noreferrer" className="text-sm text-gray-400 hover:text-red-400 transition-colors inline-flex items-center gap-1">{l}<ExternalLink size={10}/></a></li>
              ))}
            </ul>
          </div>

          {/* Newsletter + prices */}
          <div>
            <h3 className="text-xs font-black uppercase tracking-widest text-white mb-4 after:content-[''] after:block after:w-6 after:h-0.5 after:bg-red-600 after:mt-1.5">Nyhetsbrev</h3>
            <p className="text-sm text-gray-400 mb-3 leading-relaxed">Få körkortstips och erbjudanden i inkorgen.</p>
            <form onSubmit={handleSubscribe} className="flex mb-5 overflow-hidden rounded-lg border border-gray-700">
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="din@email.se" required
                className="flex-1 bg-gray-800 border-none px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none"/>
              <button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700 px-3 text-white flex items-center transition-colors">
                {loading ? '...' : <Send size={13}/>}
              </button>
            </form>
            <div className="border-t border-gray-800 pt-4">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Snabbpriser</div>
              <div className="space-y-1.5">
                {[['5 lektioner','3 750 kr'],['10 lektioner','7 500 kr'],['15 lektioner','11 250 kr'],['20 lektioner','15 000 kr'],['Risk 1','499 kr'],['Risk 2','1 990 kr']].map(([l,p])=>(
                  <div key={l} className="flex justify-between text-xs">
                    <span className="text-gray-400">{l}</span>
                    <span className="text-red-400 font-bold font-mono">{p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500">
          <span>© {new Date().getFullYear()} Alfa Trafikskola AB · Org.nr 559470-5062. Alla rättigheter förbehållna.</span>
          <div className="flex gap-4">
            <a href="#privacy" className="hover:text-red-400 transition-colors">Integritetspolicy</a>
            <a href="#terms" className="hover:text-red-400 transition-colors">Villkor</a>
            <a href="#cookies" className="hover:text-red-400 transition-colors">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
